import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    SheetImportFooter,
    SheetImportHistory,
    SheetImportOverlay,
    SheetImportSuccessBanner,
    SheetImportUrlTabs,
} from '../sheetImport/SheetImportParts';

const dateLabel = (value) => {
    const match = /^(\d{4})-(\d{2})/.exec(String(value || ''));
    return match ? `${match[2]}/01/${match[1]}` : '—';
};
const dateStamp = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
    return match ? `${match[2]}/${match[3]}/${match[1]}` : '—';
};
const money = (value) => value == null ? '—' : Number(value).toLocaleString('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
});
const targetByPercentage = (target, percentage) => {
    if (target == null || percentage == null) return null;
    const amount = Number(target) * Number(percentage);
    return Number.isFinite(amount) ? amount : null;
};
const signedMoney = (value) => {
    if (value == null || !Number.isFinite(Number(value))) return '—';
    const amount = Number(value);
    if (amount < 0) return `−${money(Math.abs(amount))}`;
    if (amount > 0) return `+${money(amount)}`;
    return money(0);
};
const percent = (value) => value == null ? '—' : `${(Number(value) * 100).toFixed(4)}%`;
const errorText = (error) => error.response?.data?.error || error.message || 'Sheet import failed.';

const ForecastDemandSheetImportModal = ({
    planningId,
    isOpen,
    canEdit,
    sheetLink,
    onClose,
    onSaved,
    onApplied,
}) => {
    const [url, setUrl] = useState('');
    const [tabs, setTabs] = useState([]);
    const [tab, setTab] = useState('');
    const [preview, setPreview] = useState(null);
    const [history, setHistory] = useState([]);
    const [applyResult, setApplyResult] = useState(null);
    const [refreshStatus, setRefreshStatus] = useState(null);
    const [changedOnly, setChangedOnly] = useState(false);
    const [loadingTabs, setLoadingTabs] = useState(false);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [savingLink, setSavingLink] = useState(false);
    const [applying, setApplying] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        setUrl(sheetLink?.spreadsheet_url || '');
        setTab(sheetLink?.tab_name || '');
        setTabs(sheetLink?.tab_name ? [{ title: sheetLink.tab_name, gid: sheetLink.sheet_gid || '' }] : []);
        setPreview(null);
        setApplyResult(null);
        setError('');
        axios.get(`/api/v1/forecast-demand-planning/${planningId}/sheet-import/history`)
            .then(({ data }) => setHistory(data || []))
            .catch((requestError) => setError(errorText(requestError)));
    }, [isOpen, planningId, sheetLink?.spreadsheet_url, sheetLink?.tab_name, sheetLink?.sheet_gid]);

    if (!isOpen) return null;

    const loadTabs = async () => {
        setLoadingTabs(true);
        setError('');
        try {
            const { data } = await axios.get(`/api/v1/forecast-demand-planning/${planningId}/sheet-import/tabs`, { params: { url } });
            setTabs(data.tabs || []);
            const selected = data.tabs?.find((item) => item.title === tab) || data.tabs?.[0];
            setTab(selected?.title || '');
        } catch (requestError) {
            setError(errorText(requestError));
        } finally {
            setLoadingTabs(false);
        }
    };

    const saveLink = async () => {
        setSavingLink(true);
        setError('');
        try {
            await axios.put(`/api/v1/forecast-demand-planning/${planningId}/sheet-link`, {
                spreadsheet_url: url,
                tab_name: tab,
            });
            try {
                await onSaved?.();
            } catch (_refreshError) {
                setError('The Google Sheet link was saved, but the plan did not refresh. Close and reopen this dialog.');
            }
        } catch (requestError) {
            setError(errorText(requestError));
        } finally {
            setSavingLink(false);
        }
    };

    const runPreview = async () => {
        setLoadingPreview(true);
        setError('');
        setApplyResult(null);
        setRefreshStatus(null);
        try {
            const { data } = await axios.post(`/api/v1/forecast-demand-planning/${planningId}/sheet-import/preview`, {
                url,
                tab_name: tab,
            });
            setPreview(data);
            const historyResponse = await axios.get(`/api/v1/forecast-demand-planning/${planningId}/sheet-import/history`);
            setHistory(historyResponse.data || []);
        } catch (requestError) {
            setError(errorText(requestError));
        } finally {
            setLoadingPreview(false);
        }
    };

    const apply = async () => {
        if (!preview?.import_id || preview.status !== 'ok' || applyResult) return;
        setApplying(true);
        setError('');
        try {
            const { data } = await axios.post(`/api/v1/forecast-demand-planning/${planningId}/sheet-import/${preview.import_id}/apply`);
            setApplyResult(data);
            try {
                await onApplied?.();
                setRefreshStatus('refreshed');
            } catch (_refreshError) {
                setRefreshStatus('failed');
            }
            const historyResponse = await axios.get(`/api/v1/forecast-demand-planning/${planningId}/sheet-import/history`);
            setHistory(historyResponse.data || []);
        } catch (requestError) {
            setError(errorText(requestError));
        } finally {
            setApplying(false);
        }
    };

    const months = preview?.month_comparison || [];
    const visibleMonths = changedOnly
        ? months.filter((month) => month.changed || month.closed)
        : months;
    const receiptId = applyResult?.import_id || preview?.import_id;
    const readbackStatus = applyResult?.readback?.status || '—';
    const linkedBudgetRows = applyResult?.linked_budgets || preview?.linked_budgets || [];

    return (
        <SheetImportOverlay>
            <div role="dialog" aria-modal="true" aria-labelledby="demand-sheet-import-title" className="flex w-full justify-center">
                <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
                <header className="flex items-start justify-between border-b p-4">
                    <div>
                        <h2 id="demand-sheet-import-title" className="text-lg font-semibold">Google Sheet link &amp; import</h2>
                        <p className="mt-1 text-sm text-gray-600">One-way import to Forecast Demand. Eadin never writes to the sheet.</p>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Close sheet import" className="rounded border px-3 py-1 text-sm">Close</button>
                </header>
                <div className="min-h-0 flex-1 space-y-4 overflow-auto p-4">
                    {error && <div role="alert" className="rounded border border-red-300 bg-red-50 p-2 text-sm text-red-800">{error}</div>}
                    {applyResult && (
                        <SheetImportSuccessBanner tone={readbackStatus === 'PASS' ? 'success' : 'error'}>
                            <h3 className="font-semibold">Import applied · receipt #{applyResult.import_id} · Readback {readbackStatus}</h3>
                            <p className="mt-1">Plan changes recorded: {applyResult.change_count || 0}.</p>
                            {applyResult.message && <p>{applyResult.message}</p>}
                            {refreshStatus && <p>{refreshStatus === 'refreshed' ? 'Forecast Demand plan refreshed.' : "Applied, but the plan didn't refresh; reload the page."}</p>}
                        </SheetImportSuccessBanner>
                    )}
                    <SheetImportUrlTabs
                        url={url}
                        onUrlChange={(value) => { setUrl(value); setPreview(null); setTabs([]); }}
                        tabs={tabs}
                        tab={tab}
                        onTabChange={setTab}
                        onLoadTabs={loadTabs}
                        loadingTabs={loadingTabs}
                        onPreview={runPreview}
                        loadingPreview={loadingPreview}
                        canPreview={Boolean(url && tab)}
                        onSaveLink={saveLink}
                        savingLink={savingLink}
                        canEdit={canEdit}
                    />
                    {preview && (
                        <section className="space-y-4">
                            {preview.status !== 'ok' && (
                                <div role="alert" className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-900">
                                    <h3 className="font-semibold">Preview needs attention</h3>
                                    <ul className="mt-1 list-disc pl-5">{(preview.reasons || []).map((reason, index) => <li key={index}>{reason}</li>)}</ul>
                                </div>
                            )}
                            <div className="grid gap-3 md:grid-cols-3">
                                <div className="rounded border p-3"><h3 className="font-semibold">Annual gross target</h3><p>{money(preview.annual?.current)} → {money(preview.annual?.proposed)}</p></div>
                                {preview.channel_comparison?.map((channel) => {
                                    const currentAmount = targetByPercentage(preview.annual?.current, channel.current_pct);
                                    const proposedAmount = targetByPercentage(preview.annual?.proposed, channel.proposed_pct);
                                    const delta = currentAmount == null || proposedAmount == null
                                        ? null
                                        : proposedAmount - currentAmount;
                                    return (
                                        <div key={channel.channel} className="rounded border p-3">
                                            <h3 className="font-semibold">{channel.channel}</h3>
                                            <p>{percent(channel.current_pct)} → {percent(channel.proposed_pct)}</p>
                                            <p className="mt-1 text-sm">
                                                {money(currentAmount)} → {money(proposedAmount)} · {signedMoney(delta)}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                            {preview.warnings?.length > 0 && <ul className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">{preview.warnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul>}
                            <section className="overflow-x-auto rounded border">
                                <div className="flex items-center justify-between border-b p-3">
                                    <h3 className="font-semibold">Monthly allocation</h3>
                                    <label className="text-sm"><input type="checkbox" checked={changedOnly} onChange={(event) => setChangedOnly(event.target.checked)} className="mr-2" />Changed only</label>
                                </div>
                                <table className="min-w-full text-sm">
                                    <thead className="bg-gray-50"><tr><th className="p-2 text-left">Month</th><th className="p-2 text-right">Current %</th><th className="p-2 text-right">Sheet Final %</th><th className="p-2 text-right">Current target</th><th className="p-2 text-right">Proposed target</th><th className="p-2">Status</th></tr></thead>
                                    <tbody>{visibleMonths.map((month) => (
                                        <tr key={month.month} className="border-t">
                                            <td className="p-2">{dateLabel(month.month)}</td><td className="p-2 text-right">{percent(month.current_pct)}</td><td className="p-2 text-right">{percent(month.proposed_pct)}</td>
                                            <td className="p-2 text-right">{money(month.current_target)}</td><td className="p-2 text-right">{money(month.proposed_target)}</td>
                                            <td className="p-2 text-center">{month.closed ? <span className="rounded bg-amber-100 px-2 py-1 text-xs">Closed month</span> : month.changed ? 'Change' : 'Matches'}</td>
                                        </tr>
                                    ))}</tbody>
                                </table>
                            </section>
                            <div className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">{preview.demand_plan_gross_note}</div>
                            {linkedBudgetRows.length > 0 && (
                                <section className="rounded border p-3">
                                    <h3 className="font-semibold">Linked Budget impact</h3>
                                    <p className="text-sm text-gray-700">Open-month Gross Sales follows the Demand Plan. Closed months retain their actuals.</p>
                                    {linkedBudgetRows.map((budget) => (
                                        <div key={budget.id} className="mt-2 text-sm">
                                            <span className="font-medium">{budget.name || `Budget ${budget.id}`}</span>
                                            {budget.preview_totals?.gross && (
                                                budget.preview_totals.gross.after == null
                                                    ? <span> · Gross Sales currently {money(budget.preview_totals.gross.before)}; changes after Update from demand plan.</span>
                                                    : <span> · Gross Sales {money(budget.preview_totals.gross.before)} → {money(budget.preview_totals.gross.after)}</span>
                                            )}
                                            {budget.frozen_months?.length > 0 && (
                                                <span> · Closed months kept as actuals: {budget.frozen_months.map(dateLabel).join(', ')}</span>
                                            )}
                                            {budget.error && <span className="text-red-700"> · {budget.error}</span>}
                                        </div>
                                    ))}
                                </section>
                            )}
                        </section>
                    )}
                    <SheetImportHistory items={history} renderItem={(item) => (
                        <div key={item.id} className="flex justify-between border-b py-1"><span>#{item.id} · {item.status} · {item.tab_name}</span><span>{dateStamp(item.created_at)} · {item.readback?.status || 'No readback'}</span></div>
                    )} />
                </div>
                <SheetImportFooter
                    applied={Boolean(applyResult)}
                    successLabel={`Receipt #${receiptId} · Readback ${readbackStatus}`}
                    successTone={readbackStatus === 'FAIL' ? 'error' : 'success'}
                    onClose={onClose}
                    canEdit={canEdit && Boolean(preview)}
                    canApply={preview?.status === 'ok' && Boolean(preview.import_id) && !applyResult}
                    applying={applying}
                    onApply={apply}
                    applyLabel="Apply"
                />
                </div>
            </div>
        </SheetImportOverlay>
    );
};

export default ForecastDemandSheetImportModal;
